'use client';
import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { User, Lock, Building2, CheckCircle, AlertCircle, Eye, EyeOff, Mail, Phone, Loader2, Bookmark, Trash2, ExternalLink, Bell, BellOff, Plus, Filter, TrendingUp, X, Gift } from 'lucide-react';
import { toast } from 'sonner';

import { watchlistService, type WatchlistProject } from '@/lib/services/watchlistService';
import { savedFiltersService, type SavedFilter, type PriceThresholdAlert, type CreateSavedFilterInput } from '@/lib/services/savedFiltersService';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import ReferEarnDashboard from './components/ReferEarnDashboard';


interface ProfileForm {
  full_name: string;
  phone: string;
}

interface CompanyForm {
  company_name: string;
  company_role: string;
}

interface PasswordForm {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

interface NewFilterForm {
  filter_name: string;
  micro_market: string;
  project_type: string;
  min_price_sqft: string;
  max_price_sqft: string;
  project_status: string;
}

type ActiveTab = 'profile' | 'company' | 'password' | 'watchlist' | 'alerts' | 'refer-earn';

const MICRO_MARKETS = ['', 'Hinjewadi', 'Kharadi', 'Baner', 'Wakad', 'Wagholi', 'Hadapsar', 'Undri'];
const PROJECT_TYPES = ['', 'Apartment', 'Villa', 'Plotted', 'Commercial'];
const PROJECT_STATUSES = ['', 'Active Sales', 'Pre-Launch', 'Nearing Possession', 'Sold Out', 'Delivered'];

export default function MyAccountPage() {
  const { user, profile } = useAuth();
  const supabase = createClient();

  const [activeTab, setActiveTab] = useState<ActiveTab>('profile');

  // Profile form
  const [profileForm, setProfileForm] = useState<ProfileForm>({ full_name: '', phone: '' });
  const [profileLoading, setProfileLoading] = useState(false);

  // Company form
  const [companyForm, setCompanyForm] = useState<CompanyForm>({ company_name: '', company_role: '' });
  const [companyLoading, setCompanyLoading] = useState(false);

  // Password form
  const [passwordForm, setPasswordForm] = useState<PasswordForm>({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  // Watchlist
  const [watchlist, setWatchlist] = useState<WatchlistProject[]>([]);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Alerts
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [priceAlerts, setPriceAlerts] = useState<PriceThresholdAlert[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [showNewFilterForm, setShowNewFilterForm] = useState(false);
  const [newFilterForm, setNewFilterForm] = useState<NewFilterForm>({
    filter_name: '',
    micro_market: '',
    project_type: '',
    min_price_sqft: '',
    max_price_sqft: '',
    project_status: '',
  });
  const [savingFilter, setSavingFilter] = useState(false);
  const [checkingAlerts, setCheckingAlerts] = useState(false);

  // Populate forms from existing data
  useEffect(() => {
    if (profile) {
      setProfileForm({
        full_name: profile.full_name || '',
        phone: profile.phone || '',
      });
    }
    if (user?.user_metadata) {
      setCompanyForm({
        company_name: user.user_metadata.company_name || '',
        company_role: user.user_metadata.company_role || '',
      });
    }
  }, [profile, user]);

  const loadWatchlist = async () => {
    if (!user) return;
    setWatchlistLoading(true);
    try {
      const data = await watchlistService.getWatchlist(user.id);
      setWatchlist(data);
    } finally {
      setWatchlistLoading(false);
    }
  };

  const loadAlerts = async () => {
    if (!user) return;
    setAlertsLoading(true);
    try {
      const [filters, thresholds] = await Promise.all([
        savedFiltersService.getSavedFilters(user.id),
        savedFiltersService.getPriceThresholdAlerts(user.id),
      ]);
      setSavedFilters(filters);
      setPriceAlerts(thresholds);
    } finally {
      setAlertsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'watchlist') loadWatchlist();
    if (activeTab === 'alerts') loadAlerts();
  }, [activeTab, user]);

  const handleRemoveFromWatchlist = async (userId: string, projectId: string, projectName: string) => {
    setRemovingId(projectId);
    try {
      const success = await watchlistService.removeFromWatchlist(userId, projectId);
      if (success) {
        setWatchlist((prev) => prev.filter((w) => w.project_id !== projectId));
        toast.success(`Removed ${projectName} from watchlist`);
      }
    } finally {
      setRemovingId(null);
    }
  };

  const handleSaveFilter = async () => {
    if (!user || !newFilterForm.filter_name.trim()) {
      toast.error('Please enter a filter name');
      return;
    }
    setSavingFilter(true);
    try {
      const input: CreateSavedFilterInput = {
        filter_name: newFilterForm.filter_name.trim(),
        ...(newFilterForm.micro_market && { micro_market: newFilterForm.micro_market }),
        ...(newFilterForm.project_type && { project_type: newFilterForm.project_type }),
        ...(newFilterForm.min_price_sqft && { min_price_sqft: parseInt(newFilterForm.min_price_sqft) }),
        ...(newFilterForm.max_price_sqft && { max_price_sqft: parseInt(newFilterForm.max_price_sqft) }),
        ...(newFilterForm.project_status && { project_status: newFilterForm.project_status }),
        email_alerts_enabled: true,
      };
      const created = await savedFiltersService.createSavedFilter(user.id, input);
      if (created) {
        setSavedFilters((prev) => [created, ...prev]);
        setNewFilterForm({ filter_name: '', micro_market: '', project_type: '', min_price_sqft: '', max_price_sqft: '', project_status: '' });
        setShowNewFilterForm(false);
        toast.success('Filter saved — you will receive email alerts when new projects match');
      } else {
        toast.error('Failed to save filter');
      }
    } finally {
      setSavingFilter(false);
    }
  };

  const handleDeleteFilter = async (filterId: string, filterName: string) => {
    const success = await savedFiltersService.deleteSavedFilter(filterId);
    if (success) {
      setSavedFilters((prev) => prev.filter((f) => f.id !== filterId));
      toast.success(`Deleted filter "${filterName}"`);
    } else {
      toast.error('Failed to delete filter');
    }
  };

  const handleToggleFilterAlerts = async (filterId: string, currentEnabled: boolean) => {
    const success = await savedFiltersService.toggleFilterAlerts(filterId, !currentEnabled);
    if (success) {
      setSavedFilters((prev) =>
        prev.map((f) => (f.id === filterId ? { ...f, email_alerts_enabled: !currentEnabled } : f))
      );
    }
  };

  const handleDeletePriceAlert = async (alertId: string) => {
    const success = await savedFiltersService.deletePriceThresholdAlert(alertId);
    if (success) {
      setPriceAlerts((prev) => prev.filter((a) => a.id !== alertId));
      toast.success('Price alert removed');
    } else {
      toast.error('Failed to remove price alert');
    }
  };

  const handleTogglePriceAlert = async (alertId: string, currentEnabled: boolean) => {
    const success = await savedFiltersService.toggleThresholdAlerts(alertId, !currentEnabled);
    if (success) {
      setPriceAlerts((prev) =>
        prev.map((a) => (a.id === alertId ? { ...a, email_alerts_enabled: !currentEnabled } : a))
      );
    }
  };

  const handleResetPriceAlert = async (alertId: string) => {
    const success = await savedFiltersService.resetTriggeredAlert(alertId);
    if (success) {
      setPriceAlerts((prev) =>
        prev.map((a) => (a.id === alertId ? { ...a, is_triggered: false, triggered_at: undefined } : a))
      );
      toast.success('Alert reset — will notify again when threshold is crossed');
    }
  };

  const handleCheckAlerts = async () => {
    setCheckingAlerts(true);
    try {
      const res = await fetch('/api/notifications/check-alerts', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        if (data.sent > 0) {
          toast.success(`Checked alerts — ${data.sent} notification${data.sent > 1 ? 's' : ''} sent`);
        } else {
          toast.success('Checked alerts — no new matches found');
        }
        await loadAlerts();
      } else {
        toast.error(data.error || 'Failed to check alerts');
      }
    } catch {
      toast.error('Failed to check alerts');
    } finally {
      setCheckingAlerts(false);
    }
  };

  const handleProfileSave = async () => {
    if (!user) return;
    setProfileLoading(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          full_name: profileForm.full_name.trim(),
          phone: profileForm.phone.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;

      await supabase.auth.updateUser({
        data: { full_name: profileForm.full_name.trim() },
      });

      toast.success('Profile updated successfully');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleCompanySave = async () => {
    if (!user) return;
    setCompanyLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          company_name: companyForm.company_name.trim(),
          company_role: companyForm.company_role.trim(),
        },
      });
      if (error) throw error;
      toast.success('Company details updated successfully');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update company details');
    } finally {
      setCompanyLoading(false);
    }
  };

  const handlePasswordSave = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordForm.new_password,
      });
      if (error) throw error;
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Password updated successfully');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const displayName = profile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const initials = displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

  const tabs: { key: ActiveTab; label: string; icon: React.ElementType }[] = [
    { key: 'profile', label: 'Profile', icon: User },
    { key: 'company', label: 'Company', icon: Building2 },
    { key: 'password', label: 'Password', icon: Lock },
    { key: 'watchlist', label: 'Watchlist', icon: Bookmark },
    { key: 'alerts', label: 'Alerts', icon: Bell },
    { key: 'refer-earn', label: 'Refer & Earn', icon: Gift },
  ];

  const filterChips = (filter: SavedFilter) => {
    const chips: string[] = [];
    if (filter.micro_market) chips.push(filter.micro_market);
    if (filter.project_type) chips.push(filter.project_type);
    if (filter.min_price_sqft || filter.max_price_sqft) {
      const min = filter.min_price_sqft ? `₹${filter.min_price_sqft.toLocaleString('en-IN')}` : '';
      const max = filter.max_price_sqft ? `₹${filter.max_price_sqft.toLocaleString('en-IN')}` : '';
      chips.push(min && max ? `${min}–${max}/sqft` : min ? `>${min}/sqft` : `<${max}/sqft`);
    }
    if (filter.project_status) chips.push(filter.project_status);
    return chips;
  };

  return (
    <AppLayout activeRoute="/my-account">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-foreground">My Account</h1>
        <p className="text-xs text-muted-foreground mt-0.5">Manage your profile, company details, and security settings</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left — Avatar + Info card */}
        <div className="lg:w-64 shrink-0">
          <div className="bg-card border border-border rounded-xl p-5 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-amber-500/15 border-2 border-amber-500/30 flex items-center justify-center mb-3">
              <span className="text-xl font-bold text-amber-400">{initials}</span>
            </div>
            <p className="text-sm font-semibold text-foreground">{displayName}</p>
            <p className="text-xs text-muted-foreground mt-0.5 break-all">{user?.email}</p>
            {companyForm.company_name && (
              <p className="text-xs text-amber-400/80 mt-1">{companyForm.company_name}</p>
            )}
            <div className="mt-3 w-full pt-3 border-t border-border">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Account Status</span>
                <span className="flex items-center gap-1 text-emerald-400 font-medium">
                  <CheckCircle size={11} />
                  Active
                </span>
              </div>
              <div className="flex items-center justify-between text-xs mt-1.5">
                <span className="text-muted-foreground">Role</span>
                <span className="text-foreground font-medium capitalize">{profile?.role || 'Client'}</span>
              </div>
            </div>
          </div>

          {/* Tab nav */}
          <div className="mt-3 bg-card border border-border rounded-xl overflow-hidden">
            {tabs.map((tab) => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all duration-150 border-b border-border last:border-0 ${
                    activeTab === tab.key
                      ? 'bg-amber-500/10 text-amber-400' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <TabIcon size={15} className="shrink-0" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right — Form panel */}
        <div className="flex-1 min-w-0">
          <div className="bg-card border border-border rounded-xl p-6">

            {/* ── Profile Tab ── */}
            {activeTab === 'profile' && (
              <div>
                <div className="mb-5">
                  <h2 className="text-base font-semibold text-foreground">Personal Information</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Update your name, email, and phone number</p>
                </div>

                <div className="space-y-4">
                  {/* Full Name */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Full Name</label>
                    <div className="relative">
                      <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        value={profileForm.full_name}
                        onChange={(e) => setProfileForm((p) => ({ ...p, full_name: e.target.value }))}
                        placeholder="Enter your full name"
                        className="w-full pl-9 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all"
                      />
                    </div>
                  </div>

                  {/* Email (read-only) */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      Email Address
                      <span className="ml-2 text-[10px] text-muted-foreground/60 font-normal">(contact support to change)</span>
                    </label>
                    <div className="relative">
                      <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/50" />
                      <input
                        type="email"
                        value={user?.email || ''}
                        readOnly
                        className="w-full pl-9 pr-4 py-2.5 bg-muted/30 border border-border rounded-lg text-sm text-muted-foreground cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Phone Number</label>
                    <div className="relative">
                      <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="tel"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm((p) => ({ ...p, phone: e.target.value }))}
                        placeholder="+91 98765 43210"
                        className="w-full pl-9 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleProfileSave}
                    disabled={profileLoading}
                    className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-all duration-150"
                  >
                    {profileLoading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                    {profileLoading ? 'Saving…' : 'Save Profile'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Company Tab ── */}
            {activeTab === 'company' && (
              <div>
                <div className="mb-5">
                  <h2 className="text-base font-semibold text-foreground">Company Details</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Add your organisation and role for personalised insights</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Company / Organisation</label>
                    <div className="relative">
                      <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        value={companyForm.company_name}
                        onChange={(e) => setCompanyForm((p) => ({ ...p, company_name: e.target.value }))}
                        placeholder="e.g. Godrej Properties"
                        className="w-full pl-9 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Your Role / Designation</label>
                    <div className="relative">
                      <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        value={companyForm.company_role}
                        onChange={(e) => setCompanyForm((p) => ({ ...p, company_role: e.target.value }))}
                        placeholder="e.g. Research Analyst"
                        className="w-full pl-9 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all"
                      />
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 p-3 bg-amber-500/5 border border-amber-500/15 rounded-lg">
                    <AlertCircle size={13} className="text-amber-400 mt-0.5 shrink-0" />
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Company details help us tailor market reports and insights to your professional context. This information is not shared publicly.
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleCompanySave}
                    disabled={companyLoading}
                    className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-all duration-150"
                  >
                    {companyLoading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                    {companyLoading ? 'Saving…' : 'Save Company Details'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Password Tab ── */}
            {activeTab === 'password' && (
              <div>
                <div className="mb-5">
                  <h2 className="text-base font-semibold text-foreground">Change Password</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Choose a strong password with at least 8 characters</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Current Password</label>
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type={showCurrentPw ? 'text' : 'password'}
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm((p) => ({ ...p, current_password: e.target.value }))}
                        placeholder="Enter current password"
                        className="w-full pl-9 pr-10 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all"
                      />
                      <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                        {showCurrentPw ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">New Password</label>
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type={showNewPw ? 'text' : 'password'}
                        value={passwordForm.new_password}
                        onChange={(e) => setPasswordForm((p) => ({ ...p, new_password: e.target.value }))}
                        placeholder="Minimum 8 characters"
                        className="w-full pl-9 pr-10 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all"
                      />
                      <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                        {showNewPw ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    {passwordForm.new_password.length > 0 && (
                      <div className="mt-1.5 flex gap-1">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-300 ${passwordForm.new_password.length >= i * 3 ? passwordForm.new_password.length >= 12 ? 'bg-emerald-500' : passwordForm.new_password.length >= 8 ? 'bg-amber-500' : 'bg-rose-500' : 'bg-muted'}`} />
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Confirm New Password</label>
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type={showConfirmPw ? 'text' : 'password'}
                        value={passwordForm.confirm_password}
                        onChange={(e) => setPasswordForm((p) => ({ ...p, confirm_password: e.target.value }))}
                        placeholder="Re-enter new password"
                        className={`w-full pl-9 pr-10 py-2.5 bg-background border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 transition-all ${passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password ? 'border-rose-500/50 focus:border-rose-500/50 focus:ring-rose-500/20' : 'border-border focus:border-amber-500/50 focus:ring-amber-500/20'}`}
                      />
                      <button type="button" onClick={() => setShowConfirmPw(!showConfirmPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                        {showConfirmPw ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    {passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
                      <p className="mt-1 text-[11px] text-rose-400 flex items-center gap-1"><AlertCircle size={10} /> Passwords do not match</p>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handlePasswordSave}
                    disabled={passwordLoading || !passwordForm.new_password || passwordForm.new_password !== passwordForm.confirm_password}
                    className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-all duration-150"
                  >
                    {passwordLoading ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
                    {passwordLoading ? 'Updating…' : 'Update Password'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Watchlist Tab ── */}
            {activeTab === 'watchlist' && (
              <div>
                <div className="mb-5">
                  <h2 className="text-base font-semibold text-foreground">My Watchlist</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Bookmarked projects — you will receive email alerts when prices or inventory change
                  </p>
                </div>

                {watchlistLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-20 rounded-lg bg-muted/40 animate-pulse" />
                    ))}
                  </div>
                ) : watchlist.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-14 text-center">
                    <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mb-3">
                      <Bookmark size={22} className="text-amber-400/60" />
                    </div>
                    <p className="text-sm font-medium text-foreground mb-1">No bookmarks yet</p>
                    <p className="text-xs text-muted-foreground mb-4 max-w-xs">
                      Click the bookmark icon on any project card or detail page to start watching it
                    </p>
                    <Link href="/project-listings-screen" className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-medium rounded-lg transition-all">
                      Browse Projects
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {watchlist.map((entry) => {
                      const proj = entry.projects;
                      if (!proj) return null;
                      const soldPct = proj.inventory_sold_pct;
                      const soldColor = soldPct >= 80 ? 'bg-emerald-500' : soldPct >= 60 ? 'bg-amber-500' : 'bg-red-500';
                      const soldTextColor = soldPct >= 80 ? 'text-emerald-400' : soldPct >= 60 ? 'text-amber-400' : 'text-red-400';
                      return (
                        <div key={entry.id} className="flex items-center gap-3 p-3 bg-background border border-border rounded-xl hover:border-amber-500/20 transition-all">
                          <div className="relative w-16 h-14 rounded-lg overflow-hidden shrink-0 bg-muted">
                            <AppImage src={proj.image_url} alt={`${proj.project_name} by ${proj.developer_name}`} fill className="object-cover" sizes="64px" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] text-muted-foreground">{proj.developer_name}</p>
                            <p className="text-sm font-semibold text-foreground truncate">{proj.project_name}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-[11px] text-muted-foreground">{proj.location}</span>
                              <span className="text-[11px] font-medium text-sky-400">{proj.micro_market}</span>
                            </div>
                          </div>
                          <div className="hidden sm:flex flex-col items-end gap-1 shrink-0">
                            <p className="text-xs font-semibold text-foreground">₹{proj.price_per_sqft?.toLocaleString()}/sqft</p>
                            <div className="flex items-center gap-1.5">
                              <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${soldColor}`} style={{ width: `${soldPct}%` }} />
                              </div>
                              <span className={`text-[10px] font-bold ${soldTextColor}`}>{soldPct}%</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Link href={`/project-detail-screen?slug=${proj.slug}`} className="p-1.5 rounded-md text-muted-foreground hover:text-amber-400 hover:bg-amber-500/10 transition-all" title="View project">
                              <ExternalLink size={13} />
                            </Link>
                            <button onClick={() => user && handleRemoveFromWatchlist(user.id, entry.project_id, proj.project_name)} disabled={removingId === entry.project_id} className="p-1.5 rounded-md text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10 transition-all disabled:opacity-50" title="Remove from watchlist">
                              {removingId === entry.project_id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    <div className="mt-4 flex items-start gap-2.5 p-3 bg-amber-500/5 border border-amber-500/15 rounded-lg">
                      <AlertCircle size={13} className="text-amber-400 mt-0.5 shrink-0" />
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        You will receive email alerts at <span className="text-foreground font-medium">{user?.email}</span> whenever price per sqft or inventory sold percentage changes on any watched project.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Alerts Tab ── */}
            {activeTab === 'alerts' && (
              <div>
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-base font-semibold text-foreground">Email Alerts</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Get notified when new projects match your filters or prices cross thresholds</p>
                  </div>
                  <button
                    onClick={handleCheckAlerts}
                    disabled={checkingAlerts}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-muted/50 border border-border hover:border-amber-500/30 hover:text-amber-400 text-muted-foreground text-xs font-medium rounded-lg transition-all disabled:opacity-50"
                  >
                    {checkingAlerts ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
                    Check Now
                  </button>
                </div>

                {alertsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-lg bg-muted/40 animate-pulse" />)}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* ── Saved Filters Section ── */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Filter size={14} className="text-amber-400" />
                          <p className="text-sm font-semibold text-foreground">Saved Filters</p>
                          <span className="text-[10px] bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded-full font-medium">{savedFilters.length}</span>
                        </div>
                        <button
                          onClick={() => setShowNewFilterForm(!showNewFilterForm)}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/20 text-amber-400 text-xs font-medium rounded-lg transition-all"
                        >
                          {showNewFilterForm ? <X size={11} /> : <Plus size={11} />}
                          {showNewFilterForm ? 'Cancel' : 'New Filter'}
                        </button>
                      </div>

                      {/* New Filter Form */}
                      {showNewFilterForm && (
                        <div className="mb-4 p-4 bg-background border border-amber-500/20 rounded-xl space-y-3">
                          <p className="text-xs font-semibold text-foreground mb-2">Create New Filter Alert</p>
                          <div>
                            <label className="block text-[11px] font-medium text-muted-foreground mb-1">Filter Name *</label>
                            <input
                              type="text"
                              value={newFilterForm.filter_name}
                              onChange={(e) => setNewFilterForm((p) => ({ ...p, filter_name: e.target.value }))}
                              placeholder="e.g. Hinjewadi Budget Apartments"
                              className="w-full px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[11px] font-medium text-muted-foreground mb-1">Micro Market</label>
                              <select value={newFilterForm.micro_market} onChange={(e) => setNewFilterForm((p) => ({ ...p, micro_market: e.target.value }))} className="w-full px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-amber-500/50 transition-all">
                                {MICRO_MARKETS.map((m) => <option key={m} value={m}>{m || 'Any market'}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-[11px] font-medium text-muted-foreground mb-1">Project Type</label>
                              <select value={newFilterForm.project_type} onChange={(e) => setNewFilterForm((p) => ({ ...p, project_type: e.target.value }))} className="w-full px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-amber-500/50 transition-all">
                                {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t || 'Any type'}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-[11px] font-medium text-muted-foreground mb-1">Min Price (₹/sqft)</label>
                              <input type="number" value={newFilterForm.min_price_sqft} onChange={(e) => setNewFilterForm((p) => ({ ...p, min_price_sqft: e.target.value }))} placeholder="e.g. 8000" className="w-full px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-amber-500/50 transition-all" />
                            </div>
                            <div>
                              <label className="block text-[11px] font-medium text-muted-foreground mb-1">Max Price (₹/sqft)</label>
                              <input type="number" value={newFilterForm.max_price_sqft} onChange={(e) => setNewFilterForm((p) => ({ ...p, max_price_sqft: e.target.value }))} placeholder="e.g. 15000" className="w-full px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-amber-500/50 transition-all" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-muted-foreground mb-1">Project Status</label>
                            <select value={newFilterForm.project_status} onChange={(e) => setNewFilterForm((p) => ({ ...p, project_status: e.target.value }))} className="w-full px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-amber-500/50 transition-all">
                              {PROJECT_STATUSES.map((s) => <option key={s} value={s}>{s || 'Any status'}</option>)}
                            </select>
                          </div>
                          <div className="flex justify-end">
                            <button onClick={handleSaveFilter} disabled={savingFilter || !newFilterForm.filter_name.trim()} className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-all">
                              {savingFilter ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
                              {savingFilter ? 'Saving…' : 'Save Filter'}
                            </button>
                          </div>
                        </div>
                      )}

                      {savedFilters.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center border border-dashed border-border rounded-xl">
                          <Filter size={20} className="text-muted-foreground/40 mb-2" />
                          <p className="text-xs text-muted-foreground">No saved filters yet</p>
                          <p className="text-[11px] text-muted-foreground/60 mt-0.5">Create a filter to get notified about new matching projects</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {savedFilters.map((filter) => {
                            const chips = filterChips(filter);
                            return (
                              <div key={filter.id} className="flex items-start gap-3 p-3 bg-background border border-border rounded-xl hover:border-amber-500/15 transition-all">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1.5">
                                    <p className="text-sm font-semibold text-foreground truncate">{filter.filter_name}</p>
                                    {filter.email_alerts_enabled ? (
                                      <span className="flex items-center gap-1 text-[10px] text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-full shrink-0">
                                        <Bell size={9} /> Active
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded-full shrink-0">
                                        <BellOff size={9} /> Paused
                                      </span>
                                    )}
                                  </div>
                                  {chips.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {chips.map((chip, i) => (
                                        <span key={i} className="text-[10px] bg-muted/60 text-muted-foreground px-2 py-0.5 rounded-full">{chip}</span>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-[11px] text-muted-foreground/60">All projects</p>
                                  )}
                                  {filter.last_notified_at && (
                                    <p className="text-[10px] text-muted-foreground/50 mt-1">Last notified: {new Date(filter.last_notified_at).toLocaleDateString('en-IN')}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <button onClick={() => handleToggleFilterAlerts(filter.id, filter.email_alerts_enabled)} className={`p-1.5 rounded-md transition-all ${filter.email_alerts_enabled ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`} title={filter.email_alerts_enabled ? 'Pause alerts' : 'Enable alerts'}>
                                    {filter.email_alerts_enabled ? <Bell size={13} /> : <BellOff size={13} />}
                                  </button>
                                  <button onClick={() => handleDeleteFilter(filter.id, filter.filter_name)} className="p-1.5 rounded-md text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10 transition-all" title="Delete filter">
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* ── Price Threshold Alerts Section ── */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp size={14} className="text-sky-400" />
                        <p className="text-sm font-semibold text-foreground">Price Threshold Alerts</p>
                        <span className="text-[10px] bg-sky-500/15 text-sky-400 px-1.5 py-0.5 rounded-full font-medium">{priceAlerts.length}</span>
                      </div>

                      {priceAlerts.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center border border-dashed border-border rounded-xl">
                          <TrendingUp size={20} className="text-muted-foreground/40 mb-2" />
                          <p className="text-xs text-muted-foreground">No price threshold alerts</p>
                          <p className="text-[11px] text-muted-foreground/60 mt-0.5">Add a project to your watchlist to set price thresholds</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {priceAlerts.map((alert) => {
                            const proj = alert.projects;
                            return (
                              <div key={alert.id} className={`flex items-start gap-3 p-3 border rounded-xl transition-all ${alert.is_triggered ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-background border-border hover:border-sky-500/15'}`}>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="text-sm font-semibold text-foreground truncate">{proj?.project_name || 'Unknown Project'}</p>
                                    {alert.is_triggered && (
                                      <span className="flex items-center gap-1 text-[10px] text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-full shrink-0">
                                        <CheckCircle size={9} /> Triggered
                                      </span>
                                    )}
                                    {!alert.email_alerts_enabled && (
                                      <span className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded-full shrink-0">
                                        <BellOff size={9} /> Paused
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[11px] text-muted-foreground">
                                    Alert when price goes <span className={`font-semibold ${alert.threshold_type === 'above' ? 'text-rose-400' : 'text-emerald-400'}`}>{alert.threshold_type}</span> ₹{alert.price_per_sqft_threshold.toLocaleString('en-IN')}/sqft
                                  </p>
                                  {proj && (
                                    <p className="text-[10px] text-muted-foreground/60 mt-0.5">Current: ₹{proj.price_per_sqft?.toLocaleString('en-IN')}/sqft · {proj.micro_market}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  {alert.is_triggered && (
                                    <button onClick={() => handleResetPriceAlert(alert.id)} className="p-1.5 rounded-md text-amber-400 hover:bg-amber-500/10 transition-all" title="Reset alert">
                                      <Bell size={13} />
                                    </button>
                                  )}
                                  <button onClick={() => handleTogglePriceAlert(alert.id, alert.email_alerts_enabled)} className={`p-1.5 rounded-md transition-all ${alert.email_alerts_enabled ? 'text-sky-400 hover:bg-sky-500/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`} title={alert.email_alerts_enabled ? 'Pause alert' : 'Enable alert'}>
                                    {alert.email_alerts_enabled ? <Bell size={13} /> : <BellOff size={13} />}
                                  </button>
                                  <button onClick={() => handleDeletePriceAlert(alert.id)} className="p-1.5 rounded-md text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10 transition-all" title="Delete alert">
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Info note */}
                    <div className="flex items-start gap-2.5 p-3 bg-amber-500/5 border border-amber-500/15 rounded-lg">
                      <AlertCircle size={13} className="text-amber-400 mt-0.5 shrink-0" />
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Notifications are sent to <span className="text-foreground font-medium">{user?.email}</span>. Click <strong>Check Now</strong> to manually trigger a check, or alerts will run automatically when project data is updated.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Refer & Earn Tab ── */}
            {activeTab === 'refer-earn' && (
              <div>
                <div className="mb-5">
                  <h2 className="text-base font-semibold text-foreground">Refer & Earn</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Earn cashback by referring friends and colleagues</p>
                </div>
                <ReferEarnDashboard />
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
